import React, { useContext, useEffect, useRef, useState } from 'react';

import http from 'api/Api';

import ClientsApi from 'api/ClientsApi';

import { AuthContext } from 'Auth/AuthContext';

import Modal from 'components/Modal/Modal';

import RoundedButton from 'components/RoundedButton/RoundedButton';

import formatSubscriptionBusinessDate from 'utility/formatSubscriptionBusinessDate';

import { moveLabels } from './moveTranslations';

import { canMoveBilling } from './billingPermissions';

import styles from './BillingCapacityMove.module.scss';

import SetupRecoveryReview from './SetupRecoveryReview';

import formatRate from './formatRate';

import { resumeLabels, validApproval } from './resumeTranslations';

export { canMoveBilling };
export const capacityChanged = () => window.dispatchEvent(new Event('billing-capacity-changed'));

export function BillingMoveReview({
  subscriptionId,
  onClose,
  onEdit,
  onSuccess,
  onBusyChange,
  plan,
  purchaseReceipt,
  onOriginalMove,
}) {
  const { authState } = useContext(AuthContext) || {};
  const copy = moveLabels[authState?.user?.language === 'ar' ? 'ar' : 'en'];
  const [preview, setPreview] = useState(null);
  const [phase, setPhase] = useState('loading');
  const [revision, setRevision] = useState(0);
  const [retryPhase, setRetryPhase] = useState(null);
  const [resumePhase, setResumePhase] = useState(null);
  const [factsStale, setFactsStale] = useState(false);
  const [tokenExpired, setTokenExpired] = useState(false);
  const resumeSeen = useRef(false);
  const recoverySeen = useRef(!!purchaseReceipt);
  const busy = useRef(false);
  const requestId = useRef(null);
  useEffect(() => {
    let mounted = true;
    setPhase('loading');
    setFactsStale(true);
    setPreview((current) => current && { ...current, moveRecovery: undefined });
    requestId.current = null;
    http
      .post(`subscription/${encodeURIComponent(subscriptionId)}/billing-policy/move-preview`, {})
      .then(({ data }) => {
        if (!mounted) return;
        // Never present a preview for a different target as confirmable.
        if (String(data.target?.subscriptionId) !== String(subscriptionId)) {
          setPhase('blocked');
          return;
        }
        if (data.mode === 'setup_pending') recoverySeen.current = true;
        if (data.mode === 'move_pending' || data.approval) {
          resumeSeen.current = true;
          onOriginalMove?.();
        }
        setFactsStale(false);
        setResumePhase(null);
        setPreview(data);
        setPhase(data.status);
        if (data.status === 'configured' && (recoverySeen.current || resumeSeen.current)) {
          capacityChanged();
          onSuccess?.();
        }
      })
      .catch((error) => {
        if (!mounted) return;
        if (resumeSeen.current && [401, 403].includes(error.response?.status)) {
          setResumePhase(error.response.status === 401 ? 'unauthenticated' : 'denied');
        }
        setPhase([401, 403].includes(error.response?.status) ? 'blocked' : 'unavailable');
      });
    return () => {
      mounted = false;
    };
  }, [subscriptionId, revision]);
  useEffect(() => {
    const expiresAt = Date.parse(preview?.moveRecovery?.expiresAt);
    setTokenExpired(!Number.isFinite(expiresAt) || expiresAt <= Date.now());
    if (!Number.isFinite(expiresAt)) return undefined;
    const timeout = setTimeout(() => setTokenExpired(true), Math.max(0, expiresAt - Date.now()));
    return () => clearTimeout(timeout);
  }, [preview]);
  const resume = async () => {
    if (
      busy.current ||
      resumePhase ||
      phase !== 'pending' ||
      tokenExpired ||
      Date.parse(preview?.moveRecovery?.expiresAt) <= Date.now() ||
      preview?.moveRecovery?.action !== 'resume_move' ||
      !preview.moveRecovery.resumeToken
    )
      return;
    busy.current = true;
    onBusyChange?.(true);
    setResumePhase('sending');
    try {
      const { data } = await http.post(
        `subscription/${encodeURIComponent(subscriptionId)}/billing-policy/move-resume`,
        { resumeToken: preview.moveRecovery.resumeToken }
      );
      if (String(data.target?.subscriptionId) !== String(subscriptionId)) {
        setPreview((current) => ({ ...current, moveRecovery: undefined }));
        setFactsStale(true);
        setResumePhase('unknown');
        return;
      }
      setFactsStale(false);
      setPreview(data);
      setPhase(data.status);
      setResumePhase(data.status === 'configured' ? null : data.status === 'pending' ? 'requested' : 'blocked');
      if (data.status === 'configured') {
        capacityChanged();
        onSuccess?.();
      }
    } catch (error) {
      setFactsStale(true);
      // A capability read before account/role loss cannot remain actionable.
      setPreview((current) => ({ ...current, moveRecovery: undefined }));
      setResumePhase(
        error.response?.status === 401 ? 'unauthenticated' : error.response?.status === 403 ? 'denied' : 'unknown'
      );
    } finally {
      busy.current = false;
      onBusyChange?.(false);
    }
  };
  const refresh = () => {
    if (!busy.current) setRevision((current) => current + 1);
  };
  const confirm = async () => {
    if (busy.current || !preview?.previewId || !['ready', 'failed'].includes(phase)) return;
    busy.current = true;
    onBusyChange?.(true);
    setPhase('sending');
    if (!requestId.current) requestId.current = window.crypto.randomUUID();
    try {
      const { data } = await http.post(
        `subscription/${encodeURIComponent(subscriptionId)}/billing-policy/move-confirm`,
        {
          previewId: preview.previewId,
          requestId: requestId.current,
        }
      );
      if (String(data.target?.subscriptionId) !== String(subscriptionId)) {
        setPhase('blocked');
        return;
      }
      if (data.mode === 'move_pending' || data.approval) {
        resumeSeen.current = true;
        onOriginalMove?.();
      }
      setPreview(data);
      if (data.status === 'configured' && String(data.target?.subscriptionId) === String(subscriptionId)) {
        setPhase('success');
        capacityChanged();
        onSuccess?.();
      } else setPhase(data.status === 'pending' ? 'pending' : 'blocked');
    } catch (error) {
      const code = error.response?.data?.code;
      setPhase(
        [401, 403].includes(error.response?.status)
          ? 'blocked'
          : code === 'BILLING_PREVIEW_STALE'
          ? 'stale'
          : code === 'BILLING_PENDING'
          ? 'pending'
          : code === 'BILLING_REQUEST_CONFLICT'
          ? 'blocked'
          : 'failed'
      );
    } finally {
      busy.current = false;
      onBusyChange?.(false);
    }
  };
  const retrySetup = async () => {
    if (
      busy.current ||
      retryPhase ||
      preview?.status !== 'pending' ||
      preview.setupRecovery?.action !== 'retry_setup' ||
      !preview.target?.tenantId
    )
      return;
    busy.current = true;
    onBusyChange?.(true);
    setRetryPhase('sending');
    try {
      await ClientsApi.retryFailedCustomer(preview.target.tenantId);
      setRetryPhase('requested');
      capacityChanged();
    } catch (error) {
      setRetryPhase('unknown');
    } finally {
      busy.current = false;
      onBusyChange?.(false);
    }
  };
  if (preview && recoverySeen.current)
    return (
      <SetupRecoveryReview
        preview={preview}
        language={authState?.user?.language === 'ar' ? 'ar' : 'en'}
        plan={plan}
        receipt={purchaseReceipt}
        retryPhase={preview.status === 'configured' ? null : retryPhase}
        onRetry={retrySetup}
        onRefresh={refresh}
        onEdit={onEdit}
        onClose={onClose}
      />
    );
  const originalMove = resumeSeen.current || preview?.mode === 'move_pending' || !!preview?.approval;
  const recoveryCopy = resumeLabels[authState?.user?.language === 'ar' ? 'ar' : 'en'];
  const approved = validApproval(preview?.approval);
  const complete = phase === 'configured' || phase === 'success';
  const accessFailure = ['unauthenticated', 'denied'].includes(resumePhase);
  const otherApprover = !accessFailure && preview?.reasons?.includes('BILLING_ORIGINAL_APPROVER_REQUIRED');
  const canResume =
    originalMove &&
    approved &&
    !otherApprover &&
    phase === 'pending' &&
    preview?.moveRecovery?.action === 'resume_move' &&
    !!preview.moveRecovery.resumeToken &&
    !tokenExpired &&
    !resumePhase;
  const uncertain = !complete && !canResume && !otherApprover;
  const billingActive = complete || preview?.rates?.accessStartsAt === 'active';
  const completionUnknown = factsStale || accessFailure || (!canResume && !otherApprover);
  const showFinishEffect = canResume && billingActive;
  const money = (value) => {
    const { currency, currencyExponent } = preview?.rates || {};
    if (value == null || !currency) return '—';
    const [whole, fraction = ''] = String(value).split('.');
    const decimals = Number.isInteger(currencyExponent) ? fraction.padEnd(currencyExponent, '0') : fraction;
    return `${currency} ${whole}${decimals ? `.${decimals}` : ''}`;
  };
  const date = (value) => {
    if (!formatSubscriptionBusinessDate(value)) return '—';
    if (!originalMove) return formatSubscriptionBusinessDate(value);
    return new Intl.DateTimeFormat(authState?.user?.language === 'ar' ? 'ar' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      timeZone: 'UTC',
      calendar: 'gregory',
      numberingSystem: authState?.user?.language === 'ar' ? 'arab' : 'latn',
    }).format(new Date(`${value}T00:00:00Z`));
  };
  const numeric = (value) => <bdi dir='ltr'>{value ?? '—'}</bdi>;
  const shortfall = preview?.reasons?.includes('BILLING_CAPACITY_SHORTFALL');
  const message =
    accessFailure && !preview
      ? recoveryCopy[resumePhase]
      : originalMove && approved && !shortfall
      ? copy.covered
      : shortfall
      ? copy.shortfall
      : phase === 'blocked'
      ? preview?.reasons?.includes('BILLING_PROVIDER_LOOKUP_UNAVAILABLE')
        ? copy.unavailable
        : preview?.reasons?.includes('BILLING_PROVIDER_UNQUALIFIED')
        ? 'This subscription contract is not supported for active-access billing.'
        : copy.blocked
      : copy[phase] || copy.covered;
  const period = preview?.rates?.period;
  const rows = [
    [copy.enabled, preview?.purchased?.active, preview?.usage?.active],
    [copy.reserved, null, preview?.usage?.legacyRemaining],
    [copy.total, preview?.purchased?.active, preview?.required?.active],
    [copy.inactiveSeats, preview?.purchased?.inactive, preview?.required?.inactive],
  ];
  return (
    <div
      className={`${styles.review} ${originalMove ? styles.originalMove : ''}`}
      role={originalMove ? 'region' : undefined}
      dir={authState?.user?.language === 'ar' ? 'rtl' : 'ltr'}
    >
      <header>
        <div className={styles.context}>{copy.context}</div>
        <h2>{originalMove ? recoveryCopy.title : copy.title}</h2>
        {preview && (
          <p>
            {originalMove ? (
              <>
                {preview.target.tenantName}
                <br />
                <strong>{plan || ''}</strong>
              </>
            ) : (
              <>
                {preview.target.tenantName}
                {plan ? ` · ${plan}` : ''}
              </>
            )}
          </p>
        )}
        {originalMove && resumePhase === 'sending' && (
          <div className={styles.requestStatus} role='status'>
            <span className={styles.waitSpinner} aria-hidden='true' />
            <div>
              <small>{recoveryCopy.request}</small>
              <strong>{recoveryCopy.waiting}</strong>
            </div>
          </div>
        )}
        {originalMove && (
          <div className={styles.outcome}>
            <div>
              <small>{recoveryCopy.completion}</small>
              <strong className={complete ? styles.positive : styles.blocked} role='status'>
                {complete ? recoveryCopy.completed : completionUnknown ? recoveryCopy.unknown : recoveryCopy.pending}
              </strong>
            </div>
            <div>
              <small>{factsStale ? recoveryCopy.lastBilling : recoveryCopy.billing}</small>
              <strong className={billingActive ? styles.positive : styles.muted}>
                {billingActive ? recoveryCopy.active : preview?.rates?.accessStartsAt ? recoveryCopy.notStarted : '—'}
              </strong>
              {preview && !billingActive && <span>{recoveryCopy.afterMove}</span>}
            </div>
          </div>
        )}
      </header>
      {/* eslint-disable jsx-a11y/no-noninteractive-tabindex -- Enable keyboard scrolling of the financial review. */}
      <section
        className={originalMove ? styles.scrollBody : undefined}
        tabIndex={originalMove ? 0 : undefined}
        aria-label={originalMove ? recoveryCopy.details : undefined}
      >
        {preview && (
          <>
            {originalMove ? (
              <div className={styles.domains}>
                <section className={styles.moveDomain}>
                  <div className={styles.domainName}>{recoveryCopy.moveDomain}</div>
                  <div className={styles.approval}>
                    <div>
                      <small>{recoveryCopy.originalApproval}</small>
                      <strong className={approved ? styles.positive : styles.blocked}>
                        {approved ? recoveryCopy.approved : recoveryCopy.unavailable}
                      </strong>
                    </div>
                    <div>
                      <small>{recoveryCopy.approver}</small>
                      {approved && (
                        <>
                          <b>{preview.approval.operator.displayName}</b>
                          <a href={`mailto:${preview.approval.operator.email}`}>
                            <bdi dir='ltr'>{preview.approval.operator.email}</bdi>
                          </a>
                        </>
                      )}
                    </div>
                  </div>
                  <div className={styles.moveCharge}>
                    <small>{copy.charge}</small>
                    <strong className={styles.positive}>{numeric(money(preview.chargeForMove))}</strong>
                  </div>
                </section>
                <section className={styles.subscriptionDomain}>
                  <div className={styles.domainName}>
                    {factsStale ? recoveryCopy.lastDomain : recoveryCopy.currentDomain}
                  </div>
                  <div className={`${styles.term} ${!preview.currentTermEndsOn ? styles.missingTerm : ''}`}>
                    <small>{factsStale ? recoveryCopy.lastTerm : copy.ends}</small>
                    <strong>
                      <bdi>
                        {preview.currentTermEndsOn ? date(preview.currentTermEndsOn) : recoveryCopy.missingTerm}
                      </bdi>
                    </strong>
                  </div>
                </section>
              </div>
            ) : (
              <div className={styles.timing}>
                <div>
                  {copy.starts}
                  <strong>
                    {['ready', 'blocked', 'stale', 'failed', 'sending'].includes(phase) ? copy.now : copy[phase]}
                  </strong>
                </div>
                <div>
                  {copy.ends}
                  <strong>
                    <bdi>{date(preview.currentTermEndsOn)}</bdi>
                  </strong>
                </div>
              </div>
            )}
            <h3>{originalMove ? recoveryCopy.rates : copy.rates}</h3>
            <div className={styles.rates}>
              {[
                ['active', copy.active, copy.existing],
                [
                  'inactive',
                  copy.inactive,
                  preview.rates?.inactive != null && Number(preview.rates.inactive) === 0 ? copy.launch : copy.approved,
                ],
              ].map(([key, label, source]) => (
                <div key={key}>
                  {label}
                  <strong>
                    {numeric(
                      formatRate(preview.rates?.[key]) == null
                        ? '—'
                        : `${preview?.rates?.currency || ''} ${formatRate(preview.rates?.[key])}`
                    )}
                  </strong>
                  <small>
                    {period ? `${period.count} ${copy[period.unit] || period.unit}` : '—'} · {source}
                  </small>
                  {key === 'inactive' && preview.rates?.inactiveEffectiveAt && (
                    <p className={styles.rateEffective}>
                      {copy.effective}: {date(preview.rates.inactiveEffectiveOn)}
                    </p>
                  )}
                </div>
              ))}
            </div>
            <h3>{copy.seats}</h3>
            <table>
              <thead>
                <tr>
                  <th scope='col'>{copy.capacity}</th>
                  <th scope='col'>{copy.purchased}</th>
                  <th scope='col'>{factsStale && originalMove ? recoveryCopy.lastNeeded : copy.needed}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map(([label, purchased, needed], index) => (
                  <tr key={label}>
                    <td>{index === 2 ? <strong>{label}</strong> : label}</td>
                    <td>{numeric(purchased)}</td>
                    <td>{numeric(needed)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className={styles.release}>
              <span>{copy.released}</span>
              <span>
                {date(originalMove ? preview.originalMoveTermEndsOn : preview.currentTermEndsOn)} · {copy.renewal}
              </span>
            </div>
          </>
        )}
        {!originalMove && (
          <p
            role={['failed', 'stale', 'unavailable', 'blocked'].includes(phase) ? 'alert' : 'status'}
            className={`${styles.notice} ${
              phase === 'ready' || phase === 'success' || (originalMove && approved && !shortfall) ? '' : styles.blocked
            }`}
          >
            {factsStale && originalMove && approved && !shortfall ? recoveryCopy.lastCovered : message}
          </p>
        )}
        {preview && (
          <>
            {!originalMove && (
              <strong>
                {copy.charge}: {numeric(money(preview.chargeForMove))}
              </strong>
            )}
            <div className={styles.preserved}>
              {[copy.invoices, copy.addons, copy.dates].map((label) => (
                <div key={label}>
                  {label}
                  <span>{copy.unchanged}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
      {/* eslint-enable jsx-a11y/no-noninteractive-tabindex */}
      {originalMove ? (
        <footer>
          {(accessFailure || otherApprover) && (
            <div className={styles.permission} role='alert'>
              <small>{recoveryCopy.access}</small>
              <strong>{accessFailure ? recoveryCopy[resumePhase] : recoveryCopy.blocked}</strong>
              {otherApprover && approved && (
                <span>
                  {preview.approval.operator.displayName} · <bdi>{preview.approval.operator.email}</bdi>
                </span>
              )}
            </div>
          )}
          {preview && (
            <div className={styles.actionContext}>
              <span>
                {recoveryCopy.moveCharge} {numeric(money(preview.chargeForMove))}
              </span>
              <span className={styles.positive}>{recoveryCopy.purchases}</span>
            </div>
          )}
          {showFinishEffect && (
            <div className={styles.footerEffect}>
              <small>{recoveryCopy.afterFinish}</small>
              <div>
                <span>{recoveryCopy.completion}</span>
                <b className={styles.positive}>{recoveryCopy.completed}</b>
              </div>
            </div>
          )}
          <div className={styles.buttons}>
            <RoundedButton text={copy.close} color='white' onClick={onClose} isDisabled={resumePhase === 'sending'} />
            {canResume && (
              <RoundedButton
                text={billingActive ? recoveryCopy.finish : recoveryCopy.resume}
                color='primary'
                onClick={resume}
              />
            )}
            {!complete && otherApprover && approved && (
              <RoundedButton
                text={`${recoveryCopy.email} ${preview.approval.operator.displayName}`}
                color='primary'
                type='link'
                href={`mailto:${preview.approval.operator.email}`}
              />
            )}
            {!complete && resumePhase !== 'sending' && (
              <RoundedButton
                text={recoveryCopy.refresh}
                color={uncertain ? 'primary' : 'white'}
                onClick={refresh}
                isDisabled={resumePhase === 'sending'}
              />
            )}
          </div>
        </footer>
      ) : (
        <footer>
          <RoundedButton
            text={['configured', 'success'].includes(phase) ? copy.close : copy.cancel}
            color='white'
            onClick={onClose}
            isDisabled={phase === 'sending'}
          />
          {!['loading', 'sending', 'success', 'ready', 'failed'].includes(phase) && (
            <RoundedButton text={copy.refresh} color='white' onClick={refresh} />
          )}
          {shortfall && onEdit && <RoundedButton text={copy.edit} color='primary' onClick={() => onEdit(preview)} />}
          {['ready', 'failed', 'sending'].includes(phase) && (
            <RoundedButton
              text={phase === 'sending' ? copy.sending : copy.confirm}
              color='primary'
              onClick={confirm}
              isDisabled={phase === 'sending'}
            />
          )}
        </footer>
      )}
    </div>
  );
}

export default function BillingCapacityMove({
  subscriptionId,
  onEdit,
  onSuccess,
  refreshKey,
  billingCapacity,
  plan,
  purchaseReceipt,
  available = true,
  onOpen,
  initiallyOpen = false,
  hideTrigger = false,
  onDismiss,
}) {
  const { authState } = useContext(AuthContext) || {};
  const [open, setOpen] = useState(initiallyOpen);
  const [sending, setSending] = useState(false);
  const [originalReview, setOriginalReview] = useState(false);
  const copy = moveLabels[authState?.user?.language === 'ar' ? 'ar' : 'en'];
  const close = () => {
    setOpen(false);
    onDismiss?.();
  };
  if (!canMoveBilling(authState?.user)) return null;
  return (
    <>
      {available && !hideTrigger && (
        <RoundedButton
          text={
            billingCapacity?.mode === 'setup_pending' || billingCapacity?.originalMovePending ? copy.setup : copy.title
          }
          color='white'
          size='sm'
          onClick={() => (onOpen ? onOpen() : setOpen(true))}
        />
      )}
      <Modal
        isOpen={open}
        showHeader={false}
        contentLabel={copy.title}
        direction={authState?.user?.language === 'ar' ? 'rtl' : 'ltr'}
        styles={{ width: originalReview ? '860px' : '720px', maxWidth: 'calc(100vw - 24px)', padding: 0 }}
        shouldCloseOnOverlayClick={false}
        onRequestClose={() => {
          if (!sending) close();
        }}
      >
        {open && (
          <BillingMoveReview
            key={`${subscriptionId}-${refreshKey || 0}`}
            subscriptionId={subscriptionId}
            plan={plan}
            purchaseReceipt={purchaseReceipt}
            onClose={close}
            onEdit={onEdit}
            onSuccess={onSuccess}
            onBusyChange={setSending}
            onOriginalMove={() => setOriginalReview(true)}
          />
        )}
      </Modal>
    </>
  );
}
